"use client";
import { useRef, useState, useEffect, useCallback, useContext } from "react";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import { useCachedExploreContent } from "@/hooks/useCachedExploreContent";
import { canManageFeatured } from "@/lib/permissions";
import { logger } from "@/lib/logger";

import { MediaDetailsModal } from "@/components/generator/MediaDetailsModal";
import { UserContext } from "@/context/UserContext";
import ExploreVideoCard from "./ExploreVideoCard";
import SimpleVideoCard from "./SimpleVideoCard";

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

// 视频缓存管理器
const videoCache = new Map();

export default function ExploreVideos({ infiniteScroll = false, userEmail }) {
  const loadingRef = useRef(null);
  const [detailVideo, setDetailVideo] = useState(null);

  // 使用单个初始加载状态
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  // 视频元素引用
  const videoRefs = useRef({});

  // 视频可见性检测
  const observerRef = useRef(null);

  // 获取当前用户信息
  const { user } = useContext(UserContext);



  // 使用新的缓存内容钩子
  const {
    items: videos,
    loading,
    hasMore,
    loadMore,
    refreshContent,
    error
  } = useCachedExploreContent({
    contentType: 'videos',
    infiniteScroll,
    featuredOnly: false, // 总是获取所有视频，支持分页
    email: null, // 可根据需要添加筛选
    itemsPerPage: 6 // 🔥 修改：从20改为6
  });

  // 监听首次数据加载
  useEffect(() => {
    if (videos.length > 0 && isFirstLoad) {
      setIsFirstLoad(false);
    }
  }, [videos]);

  // 如果加载出错，记录日志
  useEffect(() => {
    if (error) {
      logger.error("Failed to load videos:", error, "Please try again later");
    }
  }, [error]);

  // 视频可见性检测和懒加载
  useEffect(() => {
    // 视频可见性检测
    if ('IntersectionObserver' in window && videos.length > 0) {
      observerRef.current = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          const taskId = entry.target.dataset.taskId;

          if (entry.isIntersecting) {
            // 预加载视频（但不自动播放）
            const videoEl = videoRefs.current[taskId];
            if (videoEl && videoEl.preload === 'none') {
              videoEl.preload = 'metadata';
            }
          } else {
            // 如果视频在播放，则暂停
            const videoEl = videoRefs.current[taskId];
            if (videoEl && !videoEl.paused) {
              videoEl.pause();
            }
          }
        });
      }, {
        root: null,
        rootMargin: '100px',
        threshold: 0.1
      });

      // 观察所有视频元素
      Object.values(videoRefs.current).forEach(videoEl => {
        if (videoEl && observerRef.current) {
          observerRef.current.observe(videoEl);
        }
      });
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [videos.length]);



  useEffect(() => {
    logger.debug('ExploreVideos state:', {
      videosCount: videos.length,
      infiniteScroll,
      hasMore,
      loading,
      error: error || null
    });
  }, [videos, infiniteScroll, hasMore, loading, error]);

  // 修改 useEffect 以匹配新的缓存系统
  useEffect(() => {
    if (!infiniteScroll || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        if (target.isIntersecting && hasMore && !loading) {
          loadMore();
        }
      },
      {
        root: null,
        rootMargin: '100px',
        threshold: 0.1
      }
    );

    if (loadingRef.current) {
      observer.observe(loadingRef.current);
    }

    return () => {
      if (loadingRef.current) {
        observer.unobserve(loadingRef.current);
      }
    };
  }, [hasMore, loading, infiniteScroll, loadMore]);

  // 修改加载状态的判断
  if (loading && isFirstLoad) {
    return <Waiting />;
  }

  // 切换内容的精选状态
  const toggleFeatured = async (video) => {
    try {
      const response = await fetch(`/api/explore/content/featured`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'same-origin', // 确保包含cookies
        body: JSON.stringify({
          contentId: video.id,
          contentType: 'video',
          featured: !video.is_featured
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to toggle featured status');
      }

      // 成功后刷新内容
      logger.info(video.is_featured ? 'Removed from featured' : 'Added to featured');
      refreshContent();
    } catch (error) {
      logger.error('Failed to update featured status:', error);
    }
  };

  // 渲染视频详情模态框
  const renderVideoDetails = (video) => {
    if (!video) return null;
    return <MediaDetailsModal
      selectedItem={video}
      onClose={() => setDetailVideo(null)}
      source="explore"
      currentUser={user}
    />;
  };

  return (
    <div className="min-h-0">
      {detailVideo && renderVideoDetails(detailVideo)}

      <div className="relative z-10">
        <div className="mx-auto px-2 sm:px-4 lg:px-6">
          <div className="flex justify-between items-center mb-6">
            <motion.h2
              className="text-xl sm:text-2xl lg:text-3xl font-bold text-white"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
            >
            </motion.h2>
          </div>

          <motion.div
            className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2 gap-2 sm:gap-3 lg:gap-4"
            variants={container}
            initial="hidden"
            animate="show"
          >
            {videos.map((video, index) => {
              return (
                <ExploreVideoCard
                  key={`${video.task_id}-${video.id}-${index}`}
                  video={video}
                  index={index}
                  onDetailClick={(video, event) => {
                    setDetailVideo(video);
                    event.stopPropagation();
                  }}
                  onFeaturedToggle={toggleFeatured}
                  userEmail={userEmail}
                  showFeaturedToggle={true}
                  showProBadge={true}
                  showDetailButton={true}
                  videoRefs={videoRefs}
                />
              );
            })}
          </motion.div>

          {infiniteScroll && (
            <div
              ref={loadingRef}
              className="flex justify-center items-center py-8"
            >
              {loading && hasMore && (
                <div className="flex items-center px-4 py-3 rounded-xl bg-gradient-to-r from-emerald-900/40 to-green-900/40 backdrop-blur-sm border border-emerald-500/30 shadow-lg">
                  <Loader2 className="w-5 h-5 text-emerald-400 animate-spin mr-3" />
                  <p className="text-sm text-gray-300 font-medium">Loading more amazing videos...</p>
                </div>
              )}
              {!hasMore && videos.length > 0 && (
                <div className="text-center px-6 py-4 rounded-xl bg-gradient-to-r from-gray-900/60 to-black/60 backdrop-blur-sm border border-gray-700/50 shadow-lg">
                  <p className="text-sm text-gray-400 font-medium mb-1">✨ You've seen all the videos!</p>
                  <p className="text-xs text-gray-500">Check back later for more content</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Waiting() {
  return (
    <div className="flex flex-col items-center justify-center h-[50vh]">
      <div className="w-16 h-16 rounded-full bg-gradient-to-r from-emerald-400 to-green-400 opacity-80 animate-pulse mb-4" />
      <p className="text-lg font-semibold text-emerald-400 mb-2">Loading Videos</p>
      <p className="text-sm text-gray-400">Please wait while we fetch amazing content</p>
    </div>
  );
}


